/**
 * Website + ServiceApp bundles sold on the public site.
 * Super Admin owns the copy and the prices (`app_config.saas_website_bundles`).
 * These defaults match the published package: Essential / Business / Premium
 * websites paired with Start / Grow / Pro. Nothing here is tied to one customer.
 */

export const SAAS_WEBSITE_BUNDLES_KEY = 'saas_website_bundles';

export const BUNDLE_CURRENCIES = ['USD', 'GBP', 'EUR', 'AED', 'INR'] as const;
export type BundleCurrency = (typeof BUNDLE_CURRENCIES)[number];

export type MoneyMap = Partial<Record<BundleCurrency, number>>;

export type WebsiteBundle = {
  code: string;
  name: string;
  websiteTier: string;
  planCode: string;
  officeUsers: number;
  featured: boolean;
  summary: string;
  monthly: MoneyMap;
  /** Association-member monthly price. Missing currencies use memberDiscountPercent. */
  memberMonthly: MoneyMap;
  setup: MoneyMap;
};

export type WebsiteFeatureRow = {
  label: string;
  /** One cell per website tier code, in catalog.tiers order. */
  cells: string[];
};

export type WebsiteTier = {
  code: string;
  name: string;
};

export type WebsiteAddon = {
  name: string;
  price: string;
};

export type WebsiteBundleCatalog = {
  enabled: boolean;
  title: string;
  intro: string;
  trialNote: string;
  memberDiscountLabel: string;
  /** Used when a currency has no explicit member price. 0 hides the member column. */
  memberDiscountPercent: number;
  tiers: WebsiteTier[];
  bundles: WebsiteBundle[];
  features: WebsiteFeatureRow[];
  addons: WebsiteAddon[];
};

const money = (usd: number, gbp: number, eur: number, aed: number, inr: number): MoneyMap => ({
  USD: usd,
  GBP: gbp,
  EUR: eur,
  AED: aed,
  INR: inr,
});

export const DEFAULT_WEBSITE_BUNDLES: WebsiteBundleCatalog = {
  enabled: true,
  title: 'Website and app, one monthly fee',
  intro:
    'A website that takes the booking, and ServiceApp to run the job, the crew and the invoice. Hosting, care and the app are included. No large upfront payment.',
  trialNote:
    'For the first 3 months you pay only for the website. The app follows the Grow trial, then the full bundle price applies.',
  memberDiscountLabel: 'Association member',
  memberDiscountPercent: 20,
  tiers: [
    { code: 'essential', name: 'Essential' },
    { code: 'business', name: 'Business' },
    { code: 'premium', name: 'Premium' },
  ],
  bundles: [
    {
      code: 'starter',
      name: 'Site Starter',
      websiteTier: 'essential',
      planCode: 'start',
      officeUsers: 3,
      featured: false,
      summary: 'A small site and ServiceApp Start. Enough for an owner and two supervisors.',
      monthly: money(79, 62, 72, 290, 5199),
      memberMonthly: money(63, 50, 58, 232, 4299),
      setup: money(79, 62, 72, 290, 4999),
    },
    {
      code: 'business',
      name: 'Site Business',
      websiteTier: 'business',
      planCode: 'start',
      officeUsers: 3,
      featured: true,
      summary: 'One page per service, online booking and local area pages, on ServiceApp Start.',
      monthly: money(99, 79, 89, 369, 6499),
      memberMonthly: money(79, 63, 71, 295, 5199),
      setup: money(149, 119, 139, 549, 9999),
    },
    {
      code: 'business-plus',
      name: 'Site Business Plus',
      websiteTier: 'business',
      planCode: 'grow',
      officeUsers: 10,
      featured: false,
      summary: 'The Site Business website, plus Grow: staff, payroll and vehicle tracking for 10 office users.',
      monthly: money(199, 159, 179, 729, 11899),
      memberMonthly: money(159, 127, 143, 583, 9599),
      setup: money(149, 119, 139, 549, 9999),
    },
    {
      code: 'premium',
      name: 'Site Premium',
      websiteTier: 'premium',
      planCode: 'pro',
      officeUsers: 25,
      featured: false,
      summary: 'The full website and ServiceApp Pro, including quality checks and service reports.',
      monthly: money(399, 319, 369, 1469, 19699),
      memberMonthly: money(319, 255, 295, 1175, 15899),
      setup: money(299, 239, 279, 1099, 19999),
    },
  ],
  features: [
    { label: 'Pages', cells: ['Up to 6', 'Up to 12', 'Up to 20'] },
    { label: 'Design', cells: ['Template', 'Custom', 'Custom + icons'] },
    { label: 'Mobile, SSL and fast loading', cells: ['Yes', 'Yes', 'Yes'] },
    { label: 'Service pages', cells: ['1 combined', 'Up to 6', 'Up to 12'] },
    { label: 'Enquiry form into the CRM', cells: ['Yes', 'Yes', 'Yes'] },
    { label: 'WhatsApp and click to call', cells: ['Yes', 'Yes', 'Yes'] },
    { label: 'Online booking', cells: ['—', 'Yes', 'Yes'] },
    { label: 'Instant quote calculator', cells: ['—', 'Yes', 'Yes'] },
    { label: 'Corporate and contract enquiries', cells: ['—', 'Yes', 'Yes'] },
    { label: 'Reviews on the site', cells: ['—', 'Yes', 'Yes'] },
    { label: 'Google Business Profile', cells: ['—', 'Yes', 'Yes'] },
    { label: 'Before-and-after gallery', cells: ['1 gallery', 'Yes', 'Yes'] },
    { label: 'Area landing pages', cells: ['—', '3 areas', '8 areas'] },
    { label: 'Blog', cells: ['—', 'Yes', 'Yes'] },
    { label: 'Second language', cells: ['—', 'Add-on', 'Included'] },
    { label: 'Customer portal', cells: ['—', 'Yes', 'Yes'] },
    { label: 'Online payment on booking', cells: ['—', 'Add-on', 'Included'] },
    { label: 'Repeat visits explained on the site', cells: ['—', '—', 'Yes'] },
    { label: 'Content writing', cells: ['You supply', 'We write', 'We write'] },
    { label: 'Design revisions', cells: ['1 round', '2 rounds', '3 rounds'] },
    { label: 'Delivery', cells: ['2 weeks', '3–4 weeks', '5–6 weeks'] },
  ],
  addons: [
    { name: 'Second language version of the site', price: 'Monthly add-on' },
    { name: 'Online payment on booking', price: 'Monthly add-on' },
    { name: 'Extra page', price: 'One-time' },
    { name: 'Extra area landing page', price: 'One-time' },
    { name: 'Logo refresh and brand kit', price: 'One-time' },
    { name: 'Photo and video shoot', price: 'One-time' },
    { name: 'Monthly SEO and Google Business management', price: 'Monthly' },
  ],
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function moneyMap(value: unknown): MoneyMap {
  const row = asRecord(value);
  if (!row) return {};
  const out: MoneyMap = {};
  for (const code of BUNDLE_CURRENCIES) {
    const amount = Number(row[code]);
    if (Number.isFinite(amount) && amount >= 0) out[code] = Math.round(amount);
  }
  return out;
}

function cellsFor(value: unknown, width: number): string[] {
  const list = Array.isArray(value) ? value.map((cell) => String(cell ?? '').trim()) : [];
  return Array.from({ length: width }, (_, index) => list[index] ?? '—');
}

/** Accepts a Super Admin save or a missing config, and always returns a usable catalog. */
export function parseWebsiteBundles(raw: unknown): WebsiteBundleCatalog {
  const row = asRecord(raw);
  if (!row) return DEFAULT_WEBSITE_BUNDLES;

  const tierRows = Array.isArray(row.tiers) ? row.tiers : DEFAULT_WEBSITE_BUNDLES.tiers;
  const tiers = tierRows
    .map((item) => {
      const tier = asRecord(item);
      const code = String(tier?.code ?? '').trim().toLowerCase();
      const name = String(tier?.name ?? '').trim();
      return code && name ? { code, name } : null;
    })
    .filter((tier): tier is WebsiteTier => Boolean(tier));
  const safeTiers = tiers.length ? tiers : DEFAULT_WEBSITE_BUNDLES.tiers;

  const bundles = (Array.isArray(row.bundles) ? row.bundles : [])
    .map((item) => {
      const bundle = asRecord(item);
      const code = String(bundle?.code ?? '').trim().toLowerCase();
      const name = String(bundle?.name ?? '').trim();
      if (!code || !name) return null;
      const officeUsers = Number(bundle?.officeUsers);
      return {
        code,
        name,
        websiteTier: String(bundle?.websiteTier ?? safeTiers[0].code),
        planCode: String(bundle?.planCode ?? 'start').trim().toLowerCase() || 'start',
        officeUsers: Number.isFinite(officeUsers) && officeUsers > 0 ? Math.round(officeUsers) : 3,
        featured: bundle?.featured === true,
        summary: String(bundle?.summary ?? ''),
        monthly: moneyMap(bundle?.monthly),
        memberMonthly: moneyMap(bundle?.memberMonthly),
        setup: moneyMap(bundle?.setup),
      } satisfies WebsiteBundle;
    })
    .filter((bundle): bundle is WebsiteBundle => Boolean(bundle));

  const features = (Array.isArray(row.features) ? row.features : DEFAULT_WEBSITE_BUNDLES.features)
    .map((item) => {
      const feature = asRecord(item);
      const label = String(feature?.label ?? '').trim();
      if (!label) return null;
      return { label, cells: cellsFor(feature?.cells, safeTiers.length) };
    })
    .filter((feature): feature is WebsiteFeatureRow => Boolean(feature));

  const addons = (Array.isArray(row.addons) ? row.addons : DEFAULT_WEBSITE_BUNDLES.addons)
    .map((item) => {
      const addon = asRecord(item);
      const name = String(addon?.name ?? '').trim();
      if (!name) return null;
      return { name, price: String(addon?.price ?? '').trim() };
    })
    .filter((addon): addon is WebsiteAddon => Boolean(addon));

  const percent = Number(row.memberDiscountPercent);

  return {
    enabled: row.enabled !== false,
    title: String(row.title ?? '').trim() || DEFAULT_WEBSITE_BUNDLES.title,
    intro: String(row.intro ?? '').trim() || DEFAULT_WEBSITE_BUNDLES.intro,
    trialNote: String(row.trialNote ?? '').trim(),
    memberDiscountLabel: String(row.memberDiscountLabel ?? '').trim() || 'Association member',
    memberDiscountPercent: Number.isFinite(percent) ? Math.min(90, Math.max(0, Math.round(percent))) : 0,
    tiers: safeTiers,
    bundles: bundles.length ? bundles : DEFAULT_WEBSITE_BUNDLES.bundles,
    features,
    addons,
  };
}

export function bundlePrice(prices: MoneyMap, currency: string, fallback: MoneyMap = {}): number | null {
  const code = currency.trim().toUpperCase() as BundleCurrency;
  if ((BUNDLE_CURRENCIES as readonly string[]).includes(code) && typeof prices[code] === 'number') {
    return prices[code] ?? null;
  }
  if (typeof prices.USD === 'number') return prices.USD;
  if (typeof fallback.USD === 'number') return fallback.USD;
  return null;
}

export function memberBundlePrice(catalog: WebsiteBundleCatalog, bundle: WebsiteBundle, currency: string): number | null {
  const explicit = bundlePrice(bundle.memberMonthly, currency, {});
  if (bundle.memberMonthly[currency.trim().toUpperCase() as BundleCurrency] != null) return explicit;
  const standard = bundlePrice(bundle.monthly, currency);
  if (standard == null || catalog.memberDiscountPercent <= 0) return null;
  return Math.max(0, Math.round(standard * (1 - catalog.memberDiscountPercent / 100)));
}

export function formatBundleMoney(amount: number, currency: string): string {
  const code = (BUNDLE_CURRENCIES as readonly string[]).includes(currency.toUpperCase())
    ? currency.toUpperCase()
    : 'USD';
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${code} ${amount}`;
  }
}
